import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { Provider } from "../order";
import { FulfillmentGroupData } from "./fulfillment-group";
import {
  canSplitStatus,
  FulfillmentGroupStatus,
} from "./fulfillment-group-status";
import { ShopifyFulfillmentHoldReason } from "./shopify-fulfillment-schemas";

export const attributesForNewFulfillmentGroupsSchema = z.object({
  internalNote: z.string().nullish(),
  status: createEnum(FulfillmentGroupStatus),
  holdDetails: z
    .object({
      onHoldUntil: z.date().nullish(),
      reason: createEnum(ShopifyFulfillmentHoldReason),
    })
    .nullish(),
});
export type AttributesForNewFulfillmentGroups = z.infer<
  typeof attributesForNewFulfillmentGroupsSchema
>;

export const ItemToSplitSchema = z.object({
  fulfillmentOrderLineItemId: z.string(),
  quantity: z.number(),
});
export type ItemToSplit = z.infer<typeof ItemToSplitSchema>;

export function canSplitGroup(fulfillmentGroup: FulfillmentGroupData): {
  canSplit: boolean;
  disabledMessage: string | undefined;
} {
  const totalQuantity = fulfillmentGroup.items.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  const canSplit =
    totalQuantity > 1 &&
    canSplitStatus(fulfillmentGroup.status) &&
    canSplitProviders(fulfillmentGroup.providers || []);

  return {
    canSplit,
    disabledMessage: getSplitDisabledMessage(
      fulfillmentGroup.status,
      fulfillmentGroup.providers,
      totalQuantity,
    ),
  };
}

// TODO make splitting work for all providers
export function canSplitProviders(providers: Provider[]): boolean {
  const unsupportedProviders = [
    Provider.BIGCOMMERCE,
    Provider.AMAZON,
    Provider.EBAY,
    Provider.NETSUITE,
    Provider.CIN7_OMNI,
    Provider.AIMS360,
    Provider.FAIRE,
    Provider.TIKTOK,
  ];

  return !providers.some((p) => unsupportedProviders.includes(p));
}

export function getSplitDisabledMessage(
  status: FulfillmentGroupStatus | null | undefined,
  providers: Provider[] | null | undefined,
  totalQuantity: number,
): string | undefined {
  if (totalQuantity <= 1) {
    return "Cannot split shipment with only 1 item";
  }
  if (!canSplitStatus(status)) {
    return `Cannot split shipment that is ${status}`;
  }
  if (!canSplitProviders(providers || [])) {
    return "Splitting shipments from this marketplace is not yet supported";
  }
  return undefined;
}
